import { showUncaughtErrorMessage } from 'modules/alert/utils';
import { FileUploadType } from 'modules/app/modals/DuplicateFileVersion';
import { useModal } from 'modules/modals/ModalProvider';
import { useCheckReferenceDuplicationMutation } from 'modules/threads/threadsApi';
import { IReferences } from 'modules/threads/types';

const useFileDuplicationHandler = () => {
  const [checkReferenceDuplication] = useCheckReferenceDuplicationMutation();
  const { open } = useModal();
  const noop = () => {};

  const validateDuplicateFiles = async (files: File | File[] | FileList, threadId: string) => {
    if (!files) return { uniqueFiles: [], duplicateFiles: [], duplicateFileReference: undefined };

    const fileArray = files instanceof File ? [files] : Array.from(files as FileList | File[]);
    let duplicateFileReference: IReferences | undefined;
    const duplicateFiles: File[] = [];
    const results = await Promise.all(
      fileArray.map(async (file) => {
        try {
          return await checkReferenceDuplication({ file, threadId }).unwrap();
        } catch (error) {
          showUncaughtErrorMessage();
          return { filenameExists: false, reference: undefined };
        }
      })
    );

    results.forEach((res, index) => {
      if (res.filenameExists) {
        duplicateFiles.push(fileArray[index]);
        duplicateFileReference = res.reference;
      }
    });

    const uniqueFiles = fileArray.filter(
      (file) => !duplicateFiles.some((duplicateFile) => duplicateFile.name === file.name)
    );

    return { uniqueFiles, duplicateFiles, duplicateFileReference };
  };

  const showDuplicateFileModal = (
    files: File | File[] | FileList,
    id: FileUploadType,
    threadId: string,
    duplicateFileReference?: IReferences
  ) => {
    open({
      variant: 'center',
      contentLabel: 'Looks like a file with this name already exists',
      name: 'Looks like a file with this name already exists',
      modal: 'duplicateFileVersion',
      context: {
        files,
        id,
      },
      id: 'modal-duplicate-file-version',
      onClose: duplicateFileReference
        ? () => {
            open({
              variant: 'fullRight',
              contentLabel: 'Edit Reference',
              name: 'Edit Reference',
              modal: 'editReference',
              context: { referenceId: duplicateFileReference.id, threadId },
              id: 'modal-edit-reference',
            });
          }
        : noop,
    });
  };

  return { validateDuplicateFiles, showDuplicateFileModal };
};

export default useFileDuplicationHandler;
